import { z } from 'zod';
import type {
  CommissioningThreshold, CommissioningThresholdBandStructure, CommissioningThresholdDeclaration,
} from '../types/commissioning';
import { COMMISSIONING_THRESHOLD_SOURCES } from '../types/commissioning';
import {
  refineCommissioningThresholdStructure, thresholdBandStructureSchema, thresholdEffectiveBandSchema,
  thresholdsDeclarationSchema,
} from './commissioning';
import { commissioningThresholdSourceBindingsSchema, commissioningThresholdRunSummarySchema } from './commissioning-threshold-state';
import { commissioningNumericObservationBindingsSchema } from './commissioning-numeric-observations';
import { COMMISSIONING_THRESHOLD_SOURCE_FIELD, commissioningThresholdValuesSchema } from './commissioning-threshold-values';

// These schemas refer to the template contract lazily: commissioning.ts also
// contains the instance input schemas which import the runtime variant schema.
// There is one validation implementation, not a second economic contract.
const declaration: z.ZodType<CommissioningThresholdDeclaration> = z.lazy(() => thresholdsDeclarationSchema);
const effectiveBand: z.ZodType<CommissioningThreshold> = z.lazy(() => thresholdEffectiveBandSchema);
const structureBand: z.ZodType<CommissioningThresholdBandStructure> = z.lazy(() => thresholdBandStructureSchema);
const paymentSources = z.array(z.enum(COMMISSIONING_THRESHOLD_SOURCES)).max(4);

/** Riporta gli issue strutturali nel vocabolario della sezione (bands/declaration/paymentSources). */
const reportStructureIssue = (ctx: z.RefinementCtx) => (path: (string | number)[], message: string): void => {
  const [first, ...rest] = path;
  const remapped = first === 'thresholds' ? ['bands', ...rest]
    : first === 'thresholdsDeclaration' ? ['declaration', ...rest]
      : first === 'remunerationDefaults' ? rest : path;
  ctx.addIssue({ code: z.ZodIssueCode.custom, path: remapped, message });
};

/**
 * STRUTTURA Soglie del template come la vedono gli editor di gara: la stessa
 * dichiarazione, le bande (solo identità/nome/ordine/condizioni) e le fonti
 * paganti. È la parte "non modificabile in gara" del contratto.
 */
export const commissioningThresholdStructureSchema = z.object({
  declaration,
  bands: z.array(structureBand).min(1).max(100),
  paymentSources,
}).strict().superRefine((structure, ctx) => {
  refineCommissioningThresholdStructure({
    declaration: structure.declaration, bands: structure.bands, paymentSources: structure.paymentSources,
  }, reportStructureIssue(ctx));
});
export type CommissioningThresholdStructure = z.infer<typeof commissioningThresholdStructureSchema>;

/**
 * Sezione Soglie EFFETTIVA: struttura del template composta con i valori del
 * livello più specifico. È la forma che il motore consuma; non è mai salvata
 * (la producono `composeCommissioningThresholdSection` e il resolver).
 */
export const commissioningDeclarativeThresholdSectionSchema = z.object({
  declaration,
  bands: z.array(effectiveBand).min(1).max(100),
  paymentSources,
  sourceBindings: commissioningThresholdSourceBindingsSchema.optional(),
  observationBindings: commissioningNumericObservationBindingsSchema.optional(),
}).strict().superRefine((section, ctx) => {
  refineCommissioningThresholdStructure({
    declaration: section.declaration,
    bands: section.bands.map((band) => ({
      id: band.id, name: band.name,
      ...(band.categories ? { categories: band.categories.map((rule) => ({
        id: rule.id, name: rule.name, priority: rule.priority, functionId: rule.functionId,
        parameters: parameterKeys(rule.parameters),
      })) } : {}),
      ...(band.surcharges ? { surcharges: band.surcharges.map((rule) => ({
        id: rule.id, name: rule.name, functionId: rule.functionId, delta: parameterKeys(rule.delta),
      })) } : {}),
      ...(band.admission ? { admission: band.admission } : {}),
    })),
    paymentSources: section.paymentSources,
  }, reportStructureIssue(ctx));
  const declaredFields = new Set(section.declaration.sources.map((s) => COMMISSIONING_THRESHOLD_SOURCE_FIELD[s]));
  const payingFields = new Set(section.paymentSources.map((s) => COMMISSIONING_THRESHOLD_SOURCE_FIELD[s]));
  section.bands.forEach((band, index) => {
    for (const field of Object.values(COMMISSIONING_THRESHOLD_SOURCE_FIELD)) {
      if (band[field] !== undefined && !declaredFields.has(field)) ctx.addIssue({
        code: z.ZodIssueCode.custom, path: ['bands', index, field], message: `Banda ${index + 1}: la fonte "${field}" non è dichiarata`,
      });
      if (band[field] === undefined && payingFields.has(field)) ctx.addIssue({
        code: z.ZodIssueCode.custom, path: ['bands', index, field], message: `Banda ${index + 1}: valore mancante per una fonte pagante`,
      });
    }
    if (band.to !== undefined && band.to <= band.from) ctx.addIssue({
      code: z.ZodIssueCode.custom, path: ['bands', index, 'to'], message: `Banda ${index + 1}: "a" deve essere > "da"`,
    });
    if (band.to === undefined && index < section.bands.length - 1) ctx.addIssue({
      code: z.ZodIssueCode.custom, path: ['bands', index, 'to'], message: `Banda ${index + 1}: solo l’ultima banda può essere aperta`,
    });
    const previous = section.bands[index - 1];
    if (previous && previous.to !== undefined && band.from < previous.to) ctx.addIssue({
      code: z.ZodIssueCode.custom, path: ['bands', index, 'from'], message: `Banda ${index + 1}: si sovrappone alla banda precedente`,
    });
  });
});
export type CommissioningDeclarativeThresholdSection = z.infer<typeof commissioningDeclarativeThresholdSectionSchema>;

function parameterKeys(parameters: Record<string, number | undefined>): Array<'canoneMultiplier' | 'gettoneGaraMultiplier' | 'perUnitEur'> {
  const keys: Array<'canoneMultiplier' | 'gettoneGaraMultiplier' | 'perUnitEur'> = [];
  for (const key of ['canoneMultiplier', 'gettoneGaraMultiplier', 'perUnitEur'] as const) {
    if (parameters[key] !== undefined) keys.push(key);
  }
  return keys;
}

/**
 * VALORI Soglie salvati a un livello (istanza, cluster, cella driver, membro)
 * nei contenitori esistenti `aggregate_rules.declarativeThresholds`,
 * `rule_variant.declarativeThresholds`, `rule_variant.memberThresholds[id]`.
 * I binding sorgenti/osservazioni restano accanto ai valori del livello.
 */
export const commissioningThresholdLevelValuesSchema = z.object({
  values: commissioningThresholdValuesSchema,
  sourceBindings: commissioningThresholdSourceBindingsSchema.optional(),
  observationBindings: commissioningNumericObservationBindingsSchema.optional(),
}).strict();
export type CommissioningThresholdLevelValues = z.infer<typeof commissioningThresholdLevelValuesSchema>;

export const commissioningMemberThresholdsSchema = z.record(
  z.string().uuid(), commissioningThresholdLevelValuesSchema,
).superRefine((members, ctx) => {
  if (Object.keys(members).length > 5000) ctx.addIssue({
    code: z.ZodIssueCode.custom, message: 'Sono ammessi al massimo 5000 membri per assegnazione',
  });
});
export type CommissioningMemberThresholds = z.infer<typeof commissioningMemberThresholdsSchema>;

export const commissioningThresholdEditLevelSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('instance') }).strict(),
  z.object({ kind: z.literal('assignment'), assignmentId: z.string().uuid(), driverId: z.string().uuid().optional() }).strict(),
  z.object({ kind: z.literal('driver'), assignmentId: z.string().uuid(), driverId: z.string().uuid() }).strict(),
  z.object({ kind: z.literal('member'), assignmentId: z.string().uuid(), memberId: z.string().uuid(), driverId: z.string().uuid().optional() }).strict(),
]);
export type CommissioningThresholdEditLevel = z.infer<typeof commissioningThresholdEditLevelSchema>;

export const updateCommissioningDeclarativeThresholdsSchema = z.object({
  level: commissioningThresholdEditLevelSchema,
  /** Opaque revision of the editing context; never parse it as a Date. */
  revision: z.string().min(1).max(100),
  /** Valori del livello; null = azzera il livello (torna a ereditare tutto). */
  section: commissioningThresholdLevelValuesSchema.nullable(),
  recalc_ack: z.boolean().optional(),
}).strict();
export type UpdateCommissioningDeclarativeThresholdsInput = z.infer<typeof updateCommissioningDeclarativeThresholdsSchema>;

export const commissioningThresholdImportChangeSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('replace'), memberId: z.string().uuid(),
    values: commissioningThresholdLevelValuesSchema,
  }).strict(),
  z.object({ action: z.literal('reset'), memberId: z.string().uuid() }).strict(),
]);
export const previewCommissioningThresholdImportSchema = z.object({
  assignmentId: z.string().uuid(),
  changes: z.array(commissioningThresholdImportChangeSchema).min(1).max(5000),
}).strict().superRefine((input, ctx) => {
  const seen = new Set<string>();
  input.changes.forEach((change, index) => {
    if (seen.has(change.memberId)) ctx.addIssue({
      code: z.ZodIssueCode.custom, path: ['changes', index, 'memberId'],
      message: 'Membro ripetuto: ogni membro può comparire una sola volta',
    });
    seen.add(change.memberId);
  });
});
export const applyCommissioningThresholdImportSchema = z.object({
  assignmentId: z.string().uuid(),
  changes: z.array(commissioningThresholdImportChangeSchema).min(1).max(5000),
  revision: z.string().min(1).max(100),
  membershipFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  recalc_ack: z.boolean().optional(),
}).strict().superRefine((input, ctx) => {
  const check = previewCommissioningThresholdImportSchema.safeParse({
    assignmentId: input.assignmentId, changes: input.changes,
  });
  if (!check.success) for (const issue of check.error.issues) ctx.addIssue(issue);
});
export type PreviewCommissioningThresholdImportInput = z.infer<typeof previewCommissioningThresholdImportSchema>;
export type ApplyCommissioningThresholdImportInput = z.infer<typeof applyCommissioningThresholdImportSchema>;

export const commissioningThresholdOriginSchema = z.enum(['template', 'instance', 'assignment', 'driver', 'member']);
export type CommissioningThresholdOrigin = z.infer<typeof commissioningThresholdOriginSchema>;

/** Valore richiesto dalla struttura e non presente nella composizione. */
export const commissioningThresholdMissingValueSchema = z.object({
  bandId: z.string().uuid(),
  /** Percorso stabile del valore (`bands.<id>.from`, `bands.<id>.categories.<id>.perUnitEur`). */
  path: z.string().min(1),
  label: z.string().min(1),
}).strict();
export type CommissioningThresholdMissingValue = z.infer<typeof commissioningThresholdMissingValueSchema>;

export const commissioningThresholdCompletenessSchema = z.object({
  complete: z.boolean(),
  missing: z.array(commissioningThresholdMissingValueSchema),
  /** Incoerenze tra valori composti (A ≤ Da, sovrapposizioni). */
  issues: z.array(z.object({ path: z.string(), message: z.string() }).strict()),
}).strict();
export type CommissioningThresholdCompleteness = z.infer<typeof commissioningThresholdCompletenessSchema>;

/** Strato di valori con la sua origine (ordinati dal meno al più specifico). */
export const commissioningThresholdValueLayerSchema = z.object({
  origin: commissioningThresholdOriginSchema,
  values: commissioningThresholdValuesSchema,
}).strict();
export type CommissioningThresholdValueLayer = z.infer<typeof commissioningThresholdValueLayerSchema>;

export const commissioningThresholdEditorContextSchema = z.object({
  instanceId: z.string().uuid(), level: commissioningThresholdEditLevelSchema,
  revision: z.string().min(1), activeCompetition: z.boolean(),
  competitionId: z.string().uuid(),
  /** Struttura del template (sola lettura in gara); null = template senza Soglie dichiarate. */
  structure: commissioningThresholdStructureSchema.nullable(),
  /** Strati EREDITATI dal livello corrente (senza i valori locali), dal meno al più specifico. */
  inheritedLayers: z.array(commissioningThresholdValueLayerSchema),
  /** Valori salvati al livello corrente. */
  local: commissioningThresholdLevelValuesSchema.nullable(),
  /** Sezione effettiva composta (struttura + strati ereditati + locale); null se incompleta o senza struttura. */
  effective: commissioningDeclarativeThresholdSectionSchema.nullable(),
  /** Origine più specifica che contribuisce almeno un valore. */
  origin: commissioningThresholdOriginSchema.nullable(),
  completeness: commissioningThresholdCompletenessSchema,
  recalculationStatus: commissioningThresholdRunSummarySchema.optional(),
});
export type CommissioningThresholdEditorContext = z.infer<typeof commissioningThresholdEditorContextSchema>;

export const commissioningThresholdImportPreviewSchema = z.object({
  assignmentId: z.string().uuid(), revision: z.string().min(1),
  membershipFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  activeCompetition: z.boolean(),
  rows: z.array(z.object({
    memberId: z.string().uuid(), action: z.enum(['add', 'replace', 'reset']),
    previous: commissioningThresholdLevelValuesSchema.nullable(),
    values: commissioningThresholdLevelValuesSchema.nullable(),
  })),
});
export type CommissioningThresholdImportPreview = z.infer<typeof commissioningThresholdImportPreviewSchema>;

export const commissioningThresholdMutationResultSchema = z.object({
  saved: z.literal(true),
  recalc: z.enum(['not_required', 'queued', 'pending']),
  message: z.string(),
  competitionId: z.string().uuid(),
  recalcRunIds: z.array(z.string().uuid()).optional(),
});
export type CommissioningThresholdMutationResult = z.infer<typeof commissioningThresholdMutationResultSchema>;

/** Stato dei valori Soglie di un abbinamento configuratore × cluster. */
export const commissioningThresholdValuesStatusSchema = z.enum(['complete', 'incomplete', 'not_applicable']);
export type CommissioningThresholdValuesStatus = z.infer<typeof commissioningThresholdValuesStatusSchema>;

/** Optional on legacy instance reads; authenticated Soglie detail supplies the
 * editor token. Mutation outcomes distinguish a committed save from queue delivery. */
export const commissioningInstanceThresholdMetadataSchema = z.object({
  threshold_revision: z.string().min(1).optional(),
  threshold_change: commissioningThresholdMutationResultSchema.optional(),
}).strict();
export type CommissioningInstanceThresholdMetadata = z.infer<typeof commissioningInstanceThresholdMetadataSchema>;
